import React from 'react';
import { FormattedMessage } from 'react-intl';

import { FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './purchase-order-progress-bar.messages';
import styles from './purchase-order-progress-bar.styles.module.less';
import { BarChart } from '../../../../components/bar-chart/bar-chart';

/**
 * Renders a progress bar that shows received and remaining product amounts.
 * @param {object} props
 * @param {number} props.receivedItems - Number of received products.
 * @param {number} [props.rejectedItems] - Number of received products.
 * @param {number} props.totalItems - Total number of products.
 * @param {boolean} [props.renderWithoutItemsCopy] - Condition of rendering text.
 * @returns {JSX.Element} A styled progress bar with a label.
 */
const PurchaseOrderProgressBar = ({ receivedItems, rejectedItems = 0, totalItems, renderWithoutItemsCopy = false }) => {
  const reportedItems = receivedItems + rejectedItems;
  const withOverflow = totalItems - reportedItems < 0 ? reportedItems : totalItems;
  const receivedPercentage = Math.min(((receivedItems ?? 0) / withOverflow) * 100, 100);
  const rejectedPercentage = Math.min((rejectedItems / withOverflow) * 100, 100);

  const itemsProgressMessage = renderWithoutItemsCopy ? (
    `${reportedItems}/${totalItems}`
  ) : (
    <FormattedMessage {...messages.items} values={{ reportedItems, totalItems }} />
  );

  const descriptionClasses = `${styles['description-wrapper']} ${
    !receivedItems || totalItems === 0 ? styles['description-not-active'] : ''
  }`;

  const sections = [
    { size: receivedPercentage, color: 'var(--green-700)' },
    { size: rejectedPercentage, color: 'var(--red-600)' },
    {
      size: 100 - receivedPercentage - rejectedPercentage,
      color: 'var(--grey-400)',
    },
  ];

  return (
    <FlexColumn className={styles['progress-bar-wrapper']}>
      <div data-testid='purchase-order-progress-bar-test'>
        <BarChart sections={sections} />
      </div>
      <div className={descriptionClasses}>
        <Text textType={TextType.Paragraph} value={itemsProgressMessage} />
      </div>
    </FlexColumn>
  );
};

export default PurchaseOrderProgressBar;
